import React, { useEffect, useRef } from "react";

interface SpeechStreamAnimationProps {
  isDark?: boolean;
}

// Master path drawn from Left (x: -330) across floor through Center (x: 960) to Right Loop (x: 2060)
// This geometry ensures RTL glyphs remain naturally upright and cursive along the curve.
const RTL_MASTER_PATH =
  "M -330 480 C -80 620 120 720 320 780 C 540 835 780 855 960 848 C 1070 852 1170 855 1270 845 C 1520 810 1720 660 1750 500 C 1770 410 1710 330 1610 330 C 1530 330 1460 280 1450 200 C 1440 120 1500 35 1600 32 C 1700 30 1770 80 1800 180 C 1820 240 1860 340 2060 400";

// Dark Ribbon Path on the LEFT side (from x: -330 to pill left boundary at x: 880)
const RTL_RIBBON_PATH =
  "M -330 480 C -80 620 120 720 320 780 C 540 835 780 855 880 848";

// 1. Kurdish Sorani input text for the RAW non-background side (Right)
const KURDISH_INPUT_SENTENCE =
  "پلاتفۆرمەکەمان ڤیدیۆ کوردییەکانت دەکات بە دەنگی عەرەبی عێراقی ڕەسەن، بۆ ئەوەی ئەلگۆریتمەکە ڕاستەوخۆ فرۆشگاکەت پیشانی ئەو گەشتیارە عەرەبانە بدات کە توانای کڕینیان بەرزە و ئامادەن ببنە کڕیارت. ";

// 2. Authentic Iraqi Arabic dubbed output text for the BLACK RIBBON side (Left)
const IRAQI_OUTPUT_SENTENCE =
  "منصتنا تحول فيديوهاتك الكردية لصوت عربي عراقي أصلي، حتى الخوارزمية تطلع متجرك كبل گدام السياح العرب اللي عدهم قدرة شرائية عالية ومستعدين يصيرون زبائنك. ";

// Repeat sequences to ensure continuous coverage across the path with minimal layout overhead
const REPEATED_KURDISH_RAW = KURDISH_INPUT_SENTENCE.repeat(2);
const REPEATED_IRAQI_RIBBON = IRAQI_OUTPUT_SENTENCE.repeat(2);

const BARS = [
  0.3, 0.55, 0.8, 0.45, 0.95, 0.6, 1, 0.4, 0.7, 0.35, 0.85, 0.5, 0.9, 0.45,
  0.65, 0.3, 0.75, 0.55, 0.95, 0.4, 0.6, 0.35, 0.8, 0.5, 0.7, 0.3,
];

export default function SpeechStreamAnimation({ isDark = false }: SpeechStreamAnimationProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const tpRawRef = useRef<SVGTextPathElement | null>(null);
  const tpRibbonRef = useRef<SVGTextPathElement | null>(null);
  const pathRef = useRef<SVGPathElement | null>(null);

  useEffect(() => {
    // Completely disable on mobile to prevent CPU/GPU thermal load
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      return;
    }

    let animId: number | null = null;
    const tpRaw = tpRawRef.current;
    const tpRibbon = tpRibbonRef.current;
    const svgEl = svgRef.current;

    if (!tpRaw || !tpRibbon) return;

    // Single repeat lengths for both languages
    const rawSingle = tpRaw.getComputedTextLength() / 2 || 1760;
    const ribSingle = tpRibbon.getComputedTextLength() / 2 || 1570;

    let rawOffset = -rawSingle;
    let ribOffset = -ribSingle;
    const speed = 0.75;
    let isVisible = true;
    let lastTick = performance.now();
    const FRAME_INTERVAL = 1000 / 30; // 30 FPS pacing for background ambient animation

    const animate = (now: number) => {
      if (!isVisible || document.hidden) {
        animId = null;
        return;
      }

      const elapsed = now - lastTick;
      if (elapsed >= FRAME_INTERVAL) {
        const deltaFrames = Math.min(elapsed / 16.67, 3);
        lastTick = now - (elapsed % FRAME_INTERVAL);

        rawOffset -= speed * deltaFrames;
        if (rawOffset <= -2 * rawSingle) {
          rawOffset += rawSingle;
        }

        ribOffset -= speed * deltaFrames;
        if (ribOffset <= -2 * ribSingle) {
          ribOffset += ribSingle;
        }

        tpRaw.setAttribute("startOffset", rawOffset.toFixed(1));
        tpRibbon.setAttribute("startOffset", ribOffset.toFixed(1));
      }

      animId = requestAnimationFrame(animate);
    };

    const startAnim = () => {
      if (!animId && isVisible && !document.hidden) {
        lastTick = performance.now();
        animId = requestAnimationFrame(animate);
      }
    };

    const stopAnim = () => {
      if (animId) {
        cancelAnimationFrame(animId);
        animId = null;
      }
    };

    // Pause animation when hero is scrolled out of viewport
    let observer: IntersectionObserver | null = null;
    if (typeof IntersectionObserver !== "undefined" && svgEl) {
      observer = new IntersectionObserver(
        ([entry]) => {
          isVisible = entry.isIntersecting;
          if (isVisible) {
            startAnim();
          } else {
            stopAnim();
          }
        },
        { threshold: 0.05 }
      );
      observer.observe(svgEl);
    } else {
      startAnim();
    }

    const onVisibilityChange = () => {
      if (document.hidden) {
        stopAnim();
      } else if (isVisible) {
        startAnim();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      stopAnim();
      if (observer) observer.disconnect();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  return (
    <svg
      ref={svgRef}
      className="pointer-events-none absolute inset-0 h-full w-full select-none will-change-transform"
      viewBox="0 0 1920 960"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <path ref={pathRef} id="rtlMasterStreamPath" d={RTL_MASTER_PATH} />
        <path id="rtlRibbonPillPath" d={RTL_RIBBON_PATH} />

        {/* Clip for LEFT Ribbon dubbed Iraqi Arabic text (x <= 880) */}
        <clipPath id="rtlLeftRibbonClip">
          <rect x="-400" y="0" width="1280" height="960" />
        </clipPath>

        {/* Mask for RIGHT Raw Kurdish speech text (x >= 1040) */}
        <mask id="rtlRightRawMask">
          <rect x="-400" y="0" width="2700" height="960" fill="black" />
          <rect x="1040" y="0" width="1300" height="960" fill="white" />
          <linearGradient id="fadeRtlRight" x1="1040" y1="0" x2="1075" y2="0" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="black" />
            <stop offset="100%" stopColor="white" />
          </linearGradient>
          <rect x="1040" y="0" width="35" height="960" fill="url(#fadeRtlRight)" />
        </mask>
      </defs>

      {/* Layer 1: Dark Ribbon Background Stroke on Left (from x: -330 to x: 880) */}
      <use
        href="#rtlRibbonPillPath"
        fill="none"
        stroke={isDark ? "#292524" : "#1c1b17"}
        strokeWidth="62"
        strokeLinecap="round"
      />

      {/* Layer 2: Dubbed Iraqi Arabic Text in Left Dark Ribbon (x <= 880) */}
      <g clipPath="url(#rtlLeftRibbonClip)">
        <text
          className="font-sans text-[24px] font-bold tracking-tight"
          fill={isDark ? "#ffffff" : "#f5f5f4"}
        >
          <textPath ref={tpRibbonRef} href="#rtlMasterStreamPath" startOffset="0">
            {REPEATED_IRAQI_RIBBON}
          </textPath>
        </text>
      </g>

      {/* Layer 3: Raw Kurdish Sorani Speech Stream on Right (No background, enters Sound Wave Pill at x: 1040) */}
      <g mask="url(#rtlRightRawMask)">
        <text
          className="font-sans text-[22px] font-normal tracking-wide transition-colors"
          fill={isDark ? "#a8a29e" : "#78716c"}
          opacity={isDark ? 0.85 : 0.9}
        >
          <textPath ref={tpRawRef} href="#rtlMasterStreamPath" startOffset="0">
            {REPEATED_KURDISH_RAW}
          </textPath>
        </text>
      </g>

      {/* Layer 4: Central Sound Wave Transformation Pill (Exact 50% Center: x: 880..1040, center = 960) */}
      <g id="soundWavePillGroup">
        {/* Pill outer capsule container */}
        <rect
          x="880"
          y="810"
          width="160"
          height="76"
          rx="38"
          fill={isDark ? "#1c1917" : "#f8f7e4"}
          stroke={isDark ? "#383531" : "#1c1b17"}
          strokeWidth="1.75"
        />
        {/* Subtle inner highlight */}
        <rect
          x="882"
          y="812"
          width="156"
          height="72"
          rx="36"
          fill="none"
          stroke={isDark ? "#ffffff" : "#000000"}
          strokeWidth="0.75"
          opacity={isDark ? 0.08 : 0.04}
        />
        {/* Pulsing Audio Waveform Bars inside the Pill */}
        {BARS.map((h, i) => {
          const barH = Math.max(h * 36, 6);
          const x = 898 + i * 4.8;
          const y = 848 - barH / 2;
          return (
            <rect
              key={i}
              x={x}
              y={y}
              width="2.5"
              height={barH}
              rx="1.25"
              fill={isDark ? "#ffffff" : "#1c1b17"}
              className="wave-bar"
              style={{
                transformBox: "fill-box",
                transformOrigin: "center",
                animationDelay: `${(i % 8) * 0.11}s`,
                animationDuration: `${1 + (i % 5) * 0.12}s`,
              }}
            />
          );
        })}
      </g>
    </svg>
  );
}
